import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { 
  CalendarIcon, 
  Printer, 
  Truck,
  Package,
  MapPin,
  Navigation
} from 'lucide-react';

const DeliverySheetPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const [deliveryRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/api/delivery-sheet/${dateStr}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/products`, { headers: getAuthHeader() })
      ]);
      setDeliveryItems(deliveryRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching delivery sheet:', error);
      toast.error('حدث خطأ في تحميل ورقة التوصيل');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    // Build product columns
    const productColumns = products.filter(p => productTotals[p.id] > 0);
    
    const productHeaders = productColumns.map(p => 
      `<th class="product-header">${p.name}</th>`
    ).join('');
    
    const rows = deliveryItems.map((item, index) => {
      const productCells = productColumns.map(p => {
        const qty = getProductQuantity(item, p.id);
        return `<td class="product-cell ${qty > 0 ? 'has-qty' : ''}">${qty || '-'}</td>`;
      }).join('');
      
      // Calculate row total
      const rowTotal = item.total_with_tax || item.total_price || 0;
      
      return `
        <tr class="${index % 2 === 0 ? 'row-even' : 'row-odd'}">
          <td class="order-num">${item.delivery_order || index + 1}</td>
          <td class="customer-cell">
            <div class="customer-name">${item.customer_name}</div>
            ${item.shop_name ? `<div class="shop-name">🏪 ${item.shop_name}</div>` : ''}
          </td>
          <td class="area-cell">${item.area || '-'}</td>
          ${productCells}
          <td class="total-cell">${formatCurrency(rowTotal)}€</td>
          <td class="notes-cell">${item.notes || '-'}</td>
          <td class="signature-cell"></td>
        </tr>
      `;
    }).join('');
    
    // Product totals row
    const totalCells = productColumns.map(p => 
      `<td class="total-product-cell">${productTotals[p.id] || 0}</td>`
    ).join('');
    
    const printHTML = `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>ورقة التوصيل - ${formattedDate}</title>
        <style>
          @page { 
            size: A4 landscape; 
            margin: 10mm; 
          }
          
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          
          body { 
            font-family: 'Segoe UI', Tahoma, Arial, sans-serif; 
            direction: rtl; 
            background: white;
            color: #1a1a1a;
            font-size: 11px;
            line-height: 1.4;
          }
          
          /* Header Section */
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
            padding-bottom: 12px;
            border-bottom: 3px solid #d97706;
          }
          
          .header-right {
            text-align: right;
          }
          
          .header-left {
            text-align: left;
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            padding: 10px 20px;
            border-radius: 8px;
            border: 2px solid #d97706;
          }
          
          .main-title {
            font-size: 26px;
            font-weight: bold;
            color: #92400e;
            margin-bottom: 3px;
          }
          
          .sub-title {
            font-size: 14px;
            color: #78716c;
          }
          
          .date-box {
            font-size: 18px;
            font-weight: bold;
            color: #92400e;
          }
          
          .date-label {
            font-size: 11px;
            color: #78716c;
          }
          
          /* Summary Cards */
          .summary-row {
            display: flex;
            gap: 15px;
            margin-bottom: 15px;
          }
          
          .summary-card {
            flex: 1;
            padding: 10px 15px;
            border-radius: 8px;
            text-align: center;
          }
          
          .summary-card.customers {
            background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
            border: 2px solid #3b82f6;
          }
          
          .summary-card.boxes {
            background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
            border: 2px solid #10b981;
          }
          
          .summary-card.revenue {
            background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
            border: 2px solid #ec4899;
          }
          
          .summary-value {
            font-size: 22px;
            font-weight: bold;
            color: #1a1a1a;
          }
          
          .summary-label {
            font-size: 11px;
            color: #57534e;
            margin-top: 2px;
          }
          
          /* Table Styles */
          table { 
            width: 100%; 
            border-collapse: collapse;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            border-radius: 8px;
            overflow: hidden;
          }
          
          thead tr {
            background: linear-gradient(180deg, #292524 0%, #44403c 100%);
          }
          
          th {
            padding: 10px 6px;
            color: white;
            font-weight: bold;
            font-size: 11px;
            text-align: center;
            border-left: 1px solid #57534e;
          }
          
          th:first-child {
            border-right: none;
          }
          
          th:last-child {
            border-left: none;
          }
          
          .product-header {
            background: linear-gradient(180deg, #065f46 0%, #047857 100%);
            min-width: 45px;
          }
          
          /* Body Rows */
          td {
            padding: 8px 6px;
            border: 1px solid #d6d3d1;
            text-align: center;
            vertical-align: middle;
          }
          
          .row-even {
            background: #ffffff;
          }
          
          .row-odd {
            background: #fafaf9;
          }
          
          .order-num {
            font-weight: bold;
            font-size: 16px;
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%) !important;
            color: #92400e;
            width: 35px;
          }
          
          .customer-cell {
            text-align: right;
            padding-right: 10px;
            min-width: 110px;
          }
          
          .customer-name {
            font-weight: bold;
            font-size: 12px;
            color: #1c1917;
          }
          
          .shop-name {
            font-size: 10px;
            color: #78716c;
            margin-top: 2px;
          }
          
          .area-cell {
            font-size: 10px;
            color: #57534e;
            max-width: 80px;
          }
          
          .product-cell {
            font-size: 13px;
            color: #a8a29e;
            min-width: 40px;
          }
          
          .product-cell.has-qty {
            font-weight: bold;
            font-size: 15px;
            color: #166534;
            background: #dcfce7 !important;
          }
          
          .total-cell {
            font-weight: bold;
            font-size: 11px;
            color: #9333ea;
            background: #f3e8ff !important;
            min-width: 60px;
          }
          
          .notes-cell {
            font-size: 9px;
            color: #78716c;
            max-width: 70px;
            text-align: right;
          }
          
          .signature-cell {
            width: 50px;
            background: #fef3c7 !important;
          }
          
          /* Totals Row */
          .totals-row {
            background: linear-gradient(180deg, #065f46 0%, #047857 100%) !important;
          }
          
          .totals-row td {
            color: white;
            font-weight: bold;
            font-size: 12px;
            border-color: #047857;
          }
          
          .total-product-cell {
            font-size: 16px !important;
            font-weight: bold !important;
            color: white !important;
            background: transparent !important;
          }
          
          .totals-label {
            text-align: center;
            font-size: 13px;
          }
          
          /* Footer */
          .footer {
            margin-top: 15px;
            padding-top: 12px;
            border-top: 2px dashed #d6d3d1;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .footer-item {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .footer-label {
            font-size: 11px;
            color: #78716c;
          }
          
          .footer-value {
            font-size: 14px;
            font-weight: bold;
            color: #1c1917;
          }
          
          .driver-signature {
            border: 2px dashed #d6d3d1;
            padding: 15px 40px;
            border-radius: 8px;
          }
          
          .driver-signature-label {
            font-size: 11px;
            color: #78716c;
            text-align: center;
          }
          
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <!-- Header -->
        <div class="header">
          <div class="header-right">
            <div class="main-title">🚚 ورقة التوصيل</div>
            <div class="sub-title">قائمة الطلبات اليومية للتوصيل</div>
          </div>
          <div class="header-left">
            <div class="date-label">التاريخ</div>
            <div class="date-box">📅 ${formattedDate}</div>
          </div>
        </div>
        
        <!-- Summary Cards -->
        <div class="summary-row">
          <div class="summary-card customers">
            <div class="summary-value">👥 ${deliveryItems.length}</div>
            <div class="summary-label">عدد العملاء</div>
          </div>
          <div class="summary-card boxes">
            <div class="summary-value">📦 ${totalBoxes}</div>
            <div class="summary-label">إجمالي الصناديق</div>
          </div>
          <div class="summary-card revenue">
            <div class="summary-value">💰 ${formatCurrency(totalPrice)}€</div>
            <div class="summary-label">إجمالي المبيعات</div>
          </div>
        </div>
        
        <!-- Main Table -->
        <table>
          <thead>
            <tr>
              <th style="width: 35px;">#</th>
              <th style="min-width: 110px;">👤 العميل</th>
              <th style="width: 80px;">📍 المنطقة</th>
              ${productHeaders}
              <th style="width: 60px;">💵 المبلغ</th>
              <th style="width: 70px;">📝 ملاحظات</th>
              <th style="width: 50px;">✅ توقيع</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
            <tr class="totals-row">
              <td colspan="3" class="totals-label">📊 المجموع الكلي</td>
              ${totalCells}
              <td style="font-size: 13px;">${formatCurrency(totalPrice)}€</td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
        
        <!-- Footer -->
        <div class="footer">
          <div class="footer-item">
            <span class="footer-label">تم الطباعة:</span>
            <span class="footer-value">${new Date().toLocaleString('ar-EG')}</span>
          </div>
          <div class="driver-signature">
            <div class="driver-signature-label">توقيع السائق</div>
          </div>
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    if (printWindow) {
      printWindow.document.write(printHTML);
      printWindow.document.close();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formattedDate = format(selectedDate, 'yyyy/MM/dd');

  // Create product map
  const productMap = products.reduce((acc, p) => {
    acc[p.id] = p;
    return acc;
  }, {});

  // Calculate totals by product
  const productTotals = {};
  let totalPrice = 0;
  let totalBoxes = 0;

  deliveryItems.forEach(item => {
    totalPrice += item.total_with_tax || item.total_price || 0;
    if (item.items) {
      item.items.forEach(orderItem => {
        const productId = orderItem.product_id;
        if (!productTotals[productId]) {
          productTotals[productId] = 0;
        }
        productTotals[productId] += orderItem.quantity || 0;
        totalBoxes += orderItem.quantity || 0;
      });
    }
  });

  // Get product quantities for a delivery item
  const getProductQuantity = (item, productId) => {
    if (!item.items) return 0;
    const found = item.items.find(i => i.product_id === productId);
    return found ? found.quantity : 0;
  };

  return (
    <div data-testid="delivery-sheet-page" className="space-y-6">
      {/* Header - Hidden in print */}
      <div className="no-print flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-stone-900">ورقة التوصيل</h1>
          <p className="text-stone-500 mt-1">{deliveryItems.length} طلب للتوصيل</p>
        </div>
        <div className="flex gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="delivery-date-picker-btn"
                className="justify-start text-right font-normal"
              >
                <CalendarIcon className="ms-2 h-4 w-4" />
                {formattedDate}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button
            data-testid="print-btn"
            onClick={handlePrint}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
            disabled={deliveryItems.length === 0}
          >
            <Printer className="w-5 h-5 me-2" />
            طباعة
          </Button>
        </div>
      </div>

      {/* Print Header - Only shows in print */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold text-center mb-2">ورقة التوصيل</h1>
        <p className="text-center text-gray-600">{formattedDate}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 no-print">
          <div className="spinner w-10 h-10"></div>
        </div>
      ) : deliveryItems.length === 0 ? (
        <div className="empty-state no-print">
          <Truck className="empty-state-icon" />
          <h3 className="empty-state-title">لا يوجد طلبات للتوصيل</h3>
          <p className="empty-state-description">
            لا يوجد طلبات لهذا اليوم
          </p>
        </div>
      ) : (
        <>
          {/* Product Totals Summary - Shows in both screen and print */}
          <Card className="border-amber-300 bg-amber-50 print:bg-yellow-50 print:border print:border-gray-400">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-bold text-amber-800 flex items-center gap-2">
                <Package className="w-5 h-5" />
                إجمالي الصناديق المطلوبة
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {products.map((product) => {
                  const qty = productTotals[product.id] || 0;
                  if (qty === 0) return null;
                  return (
                    <div key={product.id} className="bg-white rounded-lg p-4 text-center border border-amber-200 print:border-gray-300">
                      <p className="text-3xl font-bold text-amber-700 print:text-black">{qty}</p>
                      <p className="text-sm font-medium text-stone-700 mt-1">{product.name}</p>
                    </div>
                  );
                })}
                <div className="bg-emerald-100 rounded-lg p-4 text-center border border-emerald-300 print:bg-gray-100 print:border-gray-400">
                  <p className="text-3xl font-bold text-emerald-700 print:text-black">{totalBoxes}</p>
                  <p className="text-sm font-medium text-stone-700 mt-1">إجمالي الصناديق</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Price Summary - Hidden in print for driver */}
          <div className="no-print grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalPrice)} €</p>
                <p className="text-sm text-stone-600">إجمالي المبلغ</p>
              </CardContent>
            </Card>
            <Card className="border-stone-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-stone-700">{deliveryItems.length}</p>
                <p className="text-sm text-stone-600">عدد الطلبات</p>
              </CardContent>
            </Card>
          </div>

          {/* Mobile Cards View - Hidden in print */}
          <div className="md:hidden no-print space-y-4">
            {deliveryItems.map((item, index) => (
              <Card key={item.order_id} className="border-stone-200 overflow-hidden">
                <CardContent className="p-0">
                  {/* Header with customer info */}
                  <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="bg-amber-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                          {item.delivery_order || index + 1}
                        </span>
                        <div>
                          <h4 className="font-bold text-stone-900">{item.customer_name}</h4>
                          {item.shop_name && <p className="text-sm text-stone-600">{item.shop_name}</p>}
                        </div>
                      </div>
                      <span className="font-bold text-emerald-600 text-lg">
                        {formatCurrency(item.total_with_tax || item.total_price)} €
                      </span>
                    </div>
                  </div>
                  
                  {/* Products - Main focus */}
                  <div className="p-4">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {item.items && item.items.map((orderItem, idx) => {
                        const product = productMap[orderItem.product_id];
                        return (
                          <div 
                            key={idx} 
                            className="flex items-center gap-2 bg-stone-100 px-3 py-2 rounded-lg border"
                            style={{ borderColor: product?.color || '#e5e7eb' }}
                          >
                            {product?.image_base64 ? (
                              <img src={product.image_base64} alt="" className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <Package className="w-6 h-6" style={{ color: product?.color || '#78716c' }} />
                            )}
                            <span style={{ color: product?.color || '#000' }} className="font-medium">
                              {product?.name || 'منتج'}
                            </span>
                            <span className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-sm font-bold">
                              {orderItem.quantity}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Address */}
                    {item.address && (
                      <div className="text-sm text-stone-500 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {item.address}
                        {/* Location Link */}
                        {item.latitude && item.longitude && (
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${item.latitude},${item.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-bold ms-2 hover:bg-blue-600 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Navigation className="w-3 h-3" />
                            اذهب للموقع
                          </a>
                        )}
                      </div>
                    )}
                    {/* Location only (no address) */}
                    {!item.address && item.latitude && item.longitude && (
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${item.latitude},${item.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 bg-blue-500 text-white px-3 py-1.5 rounded-full text-sm font-bold hover:bg-blue-600 transition-colors"
                      >
                        <Navigation className="w-4 h-4" />
                        اذهب للموقع
                      </a>
                    )}
                    
                    {/* Notes */}
                    {item.notes && (
                      <p className="text-sm text-amber-700 mt-2 bg-amber-50 rounded p-2 border border-amber-200">
                        ⚠️ {item.notes}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table & Print View */}
          <Card className="hidden md:block border-stone-200 print:block print:border print:border-gray-400 print:shadow-none">
            <Table className="print-table">
              <TableHeader>
                <TableRow className="bg-stone-50 print:bg-gray-200">
                  <TableHead className="text-right font-bold w-12">#</TableHead>
                  <TableHead className="text-right font-bold min-w-[250px]">الزبون والمنتجات</TableHead>
                  <TableHead className="text-right font-bold">العنوان</TableHead>
                  <TableHead className="text-right font-bold no-print">المبلغ</TableHead>
                  <TableHead className="text-right font-bold">ملاحظات</TableHead>
                  <TableHead className="text-center font-bold w-16">توقيع</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveryItems.map((item, index) => (
                  <TableRow key={item.order_id} className="hover:bg-stone-50/50 align-top">
                    <TableCell className="font-bold text-center">
                      <span className="bg-amber-500 text-white w-8 h-8 rounded-full inline-flex items-center justify-center">
                        {item.delivery_order || index + 1}
                      </span>
                    </TableCell>
                    <TableCell>
                      {/* Customer Name */}
                      <div className="font-bold text-lg mb-2">{item.customer_name}</div>
                      {item.shop_name && <div className="text-sm text-stone-500 mb-2">{item.shop_name}</div>}
                      
                      {/* Products with icons */}
                      <div className="flex flex-wrap gap-2">
                        {item.items && item.items.map((orderItem, idx) => {
                          const product = productMap[orderItem.product_id];
                          const qty = orderItem.quantity;
                          if (qty <= 0) return null;
                          return (
                            <div 
                              key={idx} 
                              className="flex items-center gap-1.5 px-2 py-1 rounded-lg border"
                              style={{ 
                                backgroundColor: `${product?.color || '#000'}10`,
                                borderColor: product?.color || '#ccc'
                              }}
                            >
                              {product?.image_base64 ? (
                                <img src={product.image_base64} alt="" className="w-6 h-6 rounded object-cover" />
                              ) : (
                                <div 
                                  className="w-5 h-5 rounded-full"
                                  style={{ backgroundColor: product?.color || '#78716c' }}
                                />
                              )}
                              <span style={{ color: product?.color || '#000' }} className="font-medium text-sm">
                                {product?.name || 'منتج'}
                              </span>
                              <span 
                                className="text-white px-1.5 py-0.5 rounded text-xs font-bold"
                                style={{ backgroundColor: product?.color || '#f59e0b' }}
                              >
                                {qty}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[150px] text-sm">
                      <div className="flex items-center gap-2">
                        <span>{item.address || '-'}</span>
                        {item.latitude && item.longitude && (
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${item.latitude},${item.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 bg-blue-500 text-white px-2 py-1 rounded text-xs font-bold hover:bg-blue-600 transition-colors no-print"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Navigation className="w-3 h-3" />
                            موقع
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-bold text-emerald-600 no-print">
                      {formatCurrency(item.total_with_tax || item.total_price)} €
                    </TableCell>
                    <TableCell className="max-w-[120px] text-sm">
                      {item.notes || '-'}
                    </TableCell>
                    <TableCell className="border-r border-stone-200">
                      {/* Empty signature area */}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow className="bg-amber-100 font-bold print:bg-gray-300">
                  <TableCell></TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap gap-2 justify-end">
                      {products.map((product) => {
                        const qty = productTotals[product.id] || 0;
                        if (qty <= 0) return null;
                        return (
                          <span 
                            key={product.id}
                            className="px-2 py-1 rounded text-sm"
                            style={{ 
                              backgroundColor: `${product?.color || '#000'}20`,
                              color: product?.color || '#000'
                            }}
                          >
                            {product.name}: <strong>{qty}</strong>
                          </span>
                        );
                      })}
                      <span className="bg-emerald-600 text-white px-3 py-1 rounded">
                        المجموع: <strong>{totalBoxes}</strong>
                      </span>
                    </div>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-emerald-700 text-lg no-print">
                    {formatCurrency(totalPrice)} €
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>

          {/* Print Footer */}
          <div className="hidden print:block mt-8 pt-4 border-t border-gray-300">
            <p className="text-center text-sm text-gray-500">
              تم إنشاء هذه الورقة بواسطة مدير المخبز - {format(new Date(), 'yyyy/MM/dd HH:mm')}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default DeliverySheetPage;
